import { useMutation, useQuery } from '@apollo/client';

import {
    faArrowRotateRight,
    faExternalLink,
    faPencil,
    faPlus,
    faSpinnerThird,
    faStethoscope,
    faTrashCan,
} from '@fortawesome/pro-regular-svg-icons';
import classnames from '../../../core/utils/classnames.tsx';
import * as React from 'react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { withIcon } from '../../../core/components/Icon.tsx';
import { GET_NAVIGATION_CHANNELS } from '../../../core/components/Navigation/internal/queries.ts';
import {
    CREATE_QUICKBOOKS_INTEGRATION,
    DELETE_QUICKBOOKS_INTEGRATION,
    GET_QUICKBOOKS_INTEGRATIONS,
    PERFORM_QUICKBOOKS_INTEGRATION_HEALTH_CHECK,
    TRIGGER_QUICKBOOKS_INTEGRATION_MASTERDATA_UPDATE,
} from '../../queries.ts';
import {
    CUSTOMER_INTEGRATION_ERP_PATH,
    CUSTOMER_INTEGRATION_QUICKBOOKS_ADD_PATH,
    CUSTOMER_INTEGRATION_QUICKBOOKS_EDIT_PATH,
    CUSTOMER_INTEGRATION_QUICKBOOKS_PATH,
} from '../../constants.ts';
import { Controller, useForm } from 'react-hook-form';
import Button from '../../../core/components/Button.tsx';
import { Link, useNavigate, useOutletContext } from 'react-router-dom';
import { url } from '../../../core/utils/link.ts';
import FormField from '../../../core/components/FormField.tsx';
import CheckboxGroup from '../../../core/components/CheckboxGroup.tsx';
import Modal from '../../../core/components/Modal.tsx';
import { useToaster } from '../../../core/components/Toast.tsx';
import { Outlet } from 'react-router';
import Layout from '../../../core/components/Layout.tsx';
import Page from '../../../core/components/Page/index.tsx';
import Table, { RowControlIconButton } from '../../../core/components/Table.tsx';
import StringField from '../../../core/components/Fields/StringField.tsx';
import { format } from 'date-fns';
import SettingsNavigation from '../../../core/components/SettingsNavigation.tsx';
import { SecretField } from '../../../core/components/Secret.tsx';
import IntegrationBreadcrumb from '../../components/IntegrationBreadcrumb.tsx';

interface QuickBooksFormModalOutletContext {
    integrations: any[];
    refetch: () => Promise<any>;
}

const AddIcon = withIcon(faPlus);
const EditIcon = withIcon(faPencil);
const DeleteIcon = withIcon(faTrashCan);
const RefreshIcon = withIcon(faArrowRotateRight);
const ExternalLinkIcon = withIcon(faExternalLink);
const LoadingIcon = withIcon(faSpinnerThird);
const HealthCheckIcon = withIcon(faStethoscope);

const FORM_DEFAULT_VALUES = {
    name: '',
    apiKey: '',
    endUserId: '',
    orderChannelIds: [],
    templateId: '',
    orderTotalItemId: '',
    discountItemId: '',
    customerTotalDiscountCustomField: '',
};

const QuickBooksFormModal = ({
    title,
    submitButtonLabel,
    onSubmit,
    onClose,
    integration,
}: {
    title: string;
    submitButtonLabel: string;
    onSubmit: (data: any) => void;
    onClose: () => void;
    integration?: any;
}) => {
    const { t } = useTranslation('integrations');

    const form = useForm({ defaultValues: FORM_DEFAULT_VALUES, mode: 'onTouched' });
    const formErrors = form.formState.errors;

    useEffect(() => {
        if (integration) {
            form.reset({
                ...FORM_DEFAULT_VALUES,
                name: integration.name,
                endUserId: integration.endUserId,
                orderChannelIds: integration.orderChannels.map((channel) => channel.id),
                templateId: integration.templateId || '',
                orderTotalItemId: integration.orderTotalItemId || '',
                discountItemId: integration.discountItemId || '',
                customerTotalDiscountCustomField: integration.customerTotalDiscountCustomField || '',
            });
        }
    }, [integration]);

    const { data: channelData } = useQuery(GET_NAVIGATION_CHANNELS, {
        fetchPolicy: 'network-only',
        notifyOnNetworkStatusChange: false,
    });
    const orderChannels = channelData?.orderProcessingChannels || [];

    let parsedExtraData;
    try {
        parsedExtraData = integration?.extraData ? JSON.parse(integration.extraData) : {};
    } catch (e) {
        parsedExtraData = {};
    }

    return (
        <Modal
            visible={true}
            onClose={onClose}
            title={title}
            buttons={
                <>
                    <Button type="button" asChild>
                        <Link to={url(CUSTOMER_INTEGRATION_QUICKBOOKS_PATH)}>{t('quickbooks.form.cancelButton')}</Link>
                    </Button>
                    <Button variant="primary" type="submit" form="modal-form">
                        {submitButtonLabel}
                    </Button>
                </>
            }
        >
            <form className="flex flex-col gap-6" onSubmit={form.handleSubmit(onSubmit)} id="modal-form">
                <Controller
                    name="name"
                    control={form.control}
                    render={({ field }) => (
                        <FormField label={t('quickbooks.form.name')} error={formErrors.name}>
                            <StringField {...field} />
                        </FormField>
                    )}
                />

                <Controller
                    name="apiKey"
                    control={form.control}
                    render={({ field }) => (
                        <FormField
                            label={t('quickbooks.form.apiKey')}
                            error={formErrors.apiKey}
                            helpText={t('secretUpdateOnly')}
                        >
                            <SecretField
                                {...field}
                                secretName="quickBooksApiKey"
                                secretId={integration?.id}
                                defaultIsVisible
                            />
                        </FormField>
                    )}
                />

                <Controller
                    name="endUserId"
                    control={form.control}
                    render={({ field }) => (
                        <FormField label={t('quickbooks.form.endUserId')} error={formErrors.endUserId}>
                            <StringField {...field} />
                        </FormField>
                    )}
                />

                <Controller
                    name="orderChannelIds"
                    control={form.control}
                    render={({ field }) => (
                        <FormField label={t('quickbooks.form.orderChannelIds')} error={formErrors.orderChannelIds}>
                            <CheckboxGroup
                                {...field}
                                options={
                                    orderChannels.map((channel) => ({
                                        value: channel.id,
                                        label: channel.name,
                                    })) || []
                                }
                                onValueChange={(value) => field.onChange({ target: { value } })}
                            />
                        </FormField>
                    )}
                />

                <Controller
                    name="templateId"
                    control={form.control}
                    render={({ field }) => (
                        <FormField label={t('quickbooks.form.templateId')} error={formErrors.templateId}>
                            <StringField {...field} />
                        </FormField>
                    )}
                />

                <Controller
                    name="orderTotalItemId"
                    control={form.control}
                    render={({ field }) => (
                        <FormField label={t('quickbooks.form.orderTotalItemId')} error={formErrors.orderTotalItemId}>
                            <StringField {...field} />
                        </FormField>
                    )}
                />

                <Controller
                    name="discountItemId"
                    control={form.control}
                    render={({ field }) => (
                        <FormField label={t('quickbooks.form.discountItemId')} error={formErrors.discountItemId}>
                            <StringField {...field} />
                        </FormField>
                    )}
                />

                <Controller
                    name="customerTotalDiscountCustomField"
                    control={form.control}
                    render={({ field }) => (
                        <FormField
                            label={t('quickbooks.form.customerTotalDiscountCustomField')}
                            error={formErrors.customerTotalDiscountCustomField}
                        >
                            <StringField {...field} />
                        </FormField>
                    )}
                />

                <FormField label={t('quickbooks.form.extraData')}>
                    <pre className="inline-block text-sm rounded bg-surface-strong p-2.5 text-content-strong">
                        {JSON.stringify(parsedExtraData, null, 4)}
                    </pre>
                </FormField>
            </form>
        </Modal>
    );
};

export const AddQuickBooksFormModal = () => {
    const { t } = useTranslation('integrations');
    const { refetch } = useOutletContext<QuickBooksFormModalOutletContext>();

    const { publishToast } = useToaster();
    const navigate = useNavigate();

    const [createIntegration] = useMutation(CREATE_QUICKBOOKS_INTEGRATION, {
        refetchQueries: [GET_QUICKBOOKS_INTEGRATIONS],
    });
    const handleCreateIntegration = async (formData) => {
        try {
            await createIntegration({
                variables: {
                    name: formData.name,
                    apiKey: formData.apiKey,
                    endUserId: formData.endUserId,
                    orderChannels: formData.orderChannelIds,
                    templateId: formData.templateId || null,
                    orderTotalItemId: formData.orderTotalItemId || null,
                    discountItemId: formData.discountItemId || null,
                    customerTotalDiscountCustomField: formData.customerTotalDiscountCustomField || null,
                },
            });
            await refetch();
            publishToast({ description: t('quickbooks.createSuccess'), status: 'success' });
            navigate(url(CUSTOMER_INTEGRATION_QUICKBOOKS_PATH));
        } catch (error) {
            console.error(error);
            publishToast({ description: t('quickbooks.createError'), status: 'error' });
        }
    };

    return (
        <QuickBooksFormModal
            title={t('quickbooks.form.createTitle')}
            submitButtonLabel={t('quickbooks.form.createButton')}
            onSubmit={handleCreateIntegration}
            onClose={() => navigate(url(CUSTOMER_INTEGRATION_QUICKBOOKS_PATH))}
        />
    );
};

const QuickBooksRow = ({ integration, refetch }: { integration: any; refetch: any }) => {
    const { t } = useTranslation('integrations');
    const { publishToast } = useToaster();

    const [isHealthCheckLoading, setIsHealthCheckLoading] = useState(false);

    const [deleteIntegration] = useMutation(DELETE_QUICKBOOKS_INTEGRATION, {
        refetchQueries: [GET_QUICKBOOKS_INTEGRATIONS],
    });
    const handleDeleteIntegration = async () => {
        try {
            await deleteIntegration({ variables: { id: integration.id } });
            await refetch();
            publishToast({ description: t('quickbooks.deleteSuccess'), status: 'success' });
        } catch (e) {
            console.error(e);
            publishToast({ description: t('quickbooks.deleteError'), status: 'error' });
        }
    };

    const [updateMasterDataIntegration] = useMutation(TRIGGER_QUICKBOOKS_INTEGRATION_MASTERDATA_UPDATE);
    const handleUpdateMasterDataIntegration = async () => {
        try {
            await updateMasterDataIntegration({ variables: { id: integration.id } });
            await refetch();
            publishToast({ description: t('quickbooks.updateMasterDataSuccess'), status: 'success' });
        } catch (e) {
            console.error(e);
            publishToast({ description: t('quickbooks.updateMasterDataError'), status: 'error' });
        }
    };

    const [performHealthCheckIntegration] = useMutation(PERFORM_QUICKBOOKS_INTEGRATION_HEALTH_CHECK);
    const handlePerformHealthCheckIntegration = async () => {
        try {
            setIsHealthCheckLoading(true);
            await performHealthCheckIntegration({ variables: { id: integration.id } });
            await refetch();
            publishToast({ description: t('quickbooks.healthCheckSuccess'), status: 'success' });
        } catch (e) {
            console.error(e);
            publishToast({ description: t('quickbooks.healthCheckError'), status: 'error' });
        }
        setIsHealthCheckLoading(false);
    };

    const channels = integration ? [...integration.orderChannels] : [];

    return (
        <>
            <Table.Row>
                <Table.Cell>{integration.name}</Table.Cell>
                <Table.Cell>
                    <div className="flex gap-1.5 flex-wrap">
                        {channels.map((channel) => (
                            <span
                                key={channel.id}
                                className="border-line-weak border border-solid bg-surface-strong flex gap-2 items-center px-1 rounded"
                            >
                                {channel.name}
                            </span>
                        ))}
                    </div>
                </Table.Cell>
                <Table.Cell>
                    {integration.masterDataUpdateActive ? (
                        <span className="flex gap-2 items-center animate-pulse">
                            <span className="text-content-success-strong">
                                <LoadingIcon spin />
                            </span>
                            <span>
                                {`${t('quickbooks.masterDataUpdateInProgress')} ${(integration.fetchProgress * 100).toFixed(2)}%`}
                            </span>
                        </span>
                    ) : integration.masterDataUpdateError ? (
                        <div className="flex flex-col gap-1">
                            <span className="text-content-danger-strong">{t('quickbooks.masterDataUpdateError')}</span>
                            <span className="text-content-medium text-sm whitespace-pre-line">
                                {integration.masterDataUpdateError}
                            </span>
                        </div>
                    ) : (
                        <span>
                            {t('quickbooks.lastMasterDataUpdate', {
                                updatedAt: format(new Date(integration.masterDataUpdatedAt), 'do MMMM yyyy, HH:mm'),
                            })}
                        </span>
                    )}
                </Table.Cell>
                <Table.Cell>
                    {integration.connectionStatus == 'CONNECTED' && !isHealthCheckLoading && (
                        <div className="flex gap-1.5 flex-wrap">
                            {' '}
                            <span className="border-line-weak border border-solid bg-surface-strong flex gap-2 items-center px-1 rounded ">
                                {integration.connectionStatus}
                            </span>
                        </div>
                    )}

                    {(integration.connectionStatus == 'IN_PROGRESS' || isHealthCheckLoading) && (
                        <div className="flex gap-1.5 flex-wrap">
                            <span className="text-content-success-strong animate-pulse">
                                <LoadingIcon spin />
                            </span>
                        </div>
                    )}

                    {integration.connectionStatus == 'DISCONNECTED' && !isHealthCheckLoading && (
                        <div className="flex gap-1.5 flex-wrap">
                            <span className="border-line-weak border border-solid bg-surface-danger-weak strong flex gap-2 items-center px-1 rounded">
                                {integration.connectionStatus}
                            </span>

                            <span className="text-content-medium text-sm whitespace-pre-line">
                                {integration.connectionStatusErrorMessage}
                            </span>
                        </div>
                    )}
                </Table.Cell>
                <Table.Cell>{new Date(integration.createdAt).toLocaleString()}</Table.Cell>
                <Table.Cell sticky="right">
                    <div className="flex gap-2">
                        <RowControlIconButton
                            onClick={handlePerformHealthCheckIntegration}
                            disabled={isHealthCheckLoading}
                        >
                            <HealthCheckIcon beat={isHealthCheckLoading} />
                        </RowControlIconButton>

                        <RowControlIconButton onClick={handleUpdateMasterDataIntegration}>
                            <RefreshIcon />
                        </RowControlIconButton>

                        <RowControlIconButton asChild>
                            <Link
                                to={url(CUSTOMER_INTEGRATION_QUICKBOOKS_EDIT_PATH, {
                                    integrationId: integration.id,
                                })}
                                className="!text-content-strong hover:!text-content-strong"
                            >
                                <EditIcon />
                            </Link>
                        </RowControlIconButton>

                        <RowControlIconButton
                            className="hover:bg-surface-danger-weak text-content-danger-strong hover:text-content-danger-strong"
                            onClick={handleDeleteIntegration}
                        >
                            <DeleteIcon />
                        </RowControlIconButton>
                    </div>
                </Table.Cell>
            </Table.Row>
        </>
    );
};

const QuickBooksIntegrationPage = () => {
    const { t } = useTranslation('integrations');

    const {
        data: data,
        loading: loading,
        refetch: refetch,
    } = useQuery(GET_QUICKBOOKS_INTEGRATIONS, {
        notifyOnNetworkStatusChange: true,
        fetchPolicy: 'no-cache',
    });
    const integrations = data?.quickbooksIntegrations || [];

    return (
        <Layout>
            <SettingsNavigation />
            <Page className="flex-1">
                <Page.Header className="justify-between">
                    <IntegrationBreadcrumb
                        backTooltip={`${t('backTooltipText')} ${t('erp.pageTitle')}`}
                        items={[
                            { label: t('erp.pageTitle'), to: url(CUSTOMER_INTEGRATION_ERP_PATH) },
                            { label: t('quickbooks.title') },
                        ]}
                    />

                    <div className="flex gap-2">
                        <Page.HeaderButton asChild>
                            <a href={t('quickbooks.documentationLink')} target={'_blank'}>
                                <span className="flex gap-2 items-center">
                                    {t('quickbooks.documentation')}
                                    <ExternalLinkIcon />{' '}
                                </span>
                            </a>
                        </Page.HeaderButton>

                        <Page.HeaderButton asChild>
                            <Link to={url(CUSTOMER_INTEGRATION_QUICKBOOKS_ADD_PATH)}>
                                <AddIcon />
                                {t('quickbooks.addButton')}
                            </Link>
                        </Page.HeaderButton>
                    </div>
                </Page.Header>

                <Page.Content lowered className="flex flex-col gap-6 relative">
                    <Table scrollable className={classnames('w-full table-auto', loading && 'opacity-50')}>
                        <Table.Head>
                            <Table.Row>
                                <Table.HeadCell className="min-w-96 w-96">{t('quickbooks.table.name')}</Table.HeadCell>
                                <Table.HeadCell>{t('quickbooks.table.channels')}</Table.HeadCell>
                                <Table.HeadCell>{t('quickbooks.table.masterDataUpdatedAt')}</Table.HeadCell>
                                <Table.HeadCell>{t('quickbooks.table.connectionStatus')}</Table.HeadCell>
                                <Table.HeadCell>{t('quickbooks.table.createdAt')}</Table.HeadCell>
                                <Table.HeadCell sticky="right" className="min-w-14 w-14" />
                            </Table.Row>
                        </Table.Head>
                        <Table.Body>
                            {integrations && integrations.length > 0 ? (
                                integrations.map((integration) => (
                                    <QuickBooksRow key={integration.id} integration={integration} refetch={refetch} />
                                ))
                            ) : (
                                <Table.Row>
                                    <Table.Cell colSpan={5} className="text-center text-content-medium">
                                        {t('quickbooks.noIntegrations')}
                                    </Table.Cell>
                                </Table.Row>
                            )}
                        </Table.Body>
                    </Table>
                </Page.Content>
            </Page>

            <Outlet context={{ integrations, refetch }} />
        </Layout>
    );
};

export default QuickBooksIntegrationPage;
